import { useEffect, useRef, useState } from "react";

import TextInput from "../../components/input/TextInput";
import styles from "./Landing.module.scss";
import { formatDate } from "../../utils/functions";
import { runGetPublicLicense } from "../../utils/services";
import runtBanner from "../../assets/runt_banner.png";
import deviceReferencePdf from "../../docs/Instructivo Captores Biometricos Bridge RUNT 2.0 (1).pdf";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const resultColumns = [
	{ key: "company", label: "Empresa" },
	{ key: "serial", label: "Serial" },
	{ key: "deviceModel", label: "Modelo" },
	{ key: "purchaseDate", label: "Fecha de compra" },
	{ key: "expirationDate", label: "Fecha de vencimiento" },
	{ key: "license", label: "Licencia" },
	{ key: "copy", label: "Copiar" },
];

const translatePublicLookupError = (error) => {
	const code = error?.code || "";
	const message = error?.message || "";

	switch (code) {
		case "LRG001":
			return "";
		case "LRT004":
			return "Licencia no encontrada.";
		case "LRL008":
			return "Su sesión ha expirado.";
		default:
			break;
	}

	if (message.includes("Cannot GET")) {
		return "No se pudo consultar la licencia.";
	}

	return message || "No se pudo consultar la licencia.";
};

if (typeof window !== "undefined") {
	window.__onRecaptchaLoaded = function () {
		window.__recaptchaReady = true;
		if (typeof window.__renderCaptchaCallback === "function") {
			window.__renderCaptchaCallback();
		}
	};
}

const Landing = () => {
	const [serialNumber, setSerialNumber] = useState("");
	const [hasSearched, setHasSearched] = useState(false);
	const [captchaToken, setCaptchaToken] = useState("");
	const [results, setResults] = useState([]);
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [copiedSerial, setCopiedSerial] = useState("");
	const [captchaLoading, setCaptchaLoading] = useState(true);
	const captchaContainerRef = useRef(null);
	const captchaWidgetIdRef = useRef(null);

	useEffect(() => {
		if (!RECAPTCHA_SITE_KEY?.trim()) {
			setErrorMessage("Falta configurar la clave de reCAPTCHA.");
			setCaptchaLoading(false);
			return undefined;
		}

		let mounted = true;
		let pollInterval = null;

		const renderCaptchaWidget = () => {
			if (!mounted) {
				return;
			}

			if (captchaWidgetIdRef.current !== null) {
				setCaptchaLoading(false);
				if (pollInterval) clearInterval(pollInterval);
				return;
			}

			const container = captchaContainerRef.current;
			if (!container) {
				return;
			}

			if (
				!window.grecaptcha ||
				typeof window.grecaptcha.render !== "function"
			) {
				return;
			}

			try {
				if (container.children.length === 0) {
					const widgetId = window.grecaptcha.render(container, {
						sitekey: RECAPTCHA_SITE_KEY.trim(),
						theme: "light",
						callback: (token) => {
							setCaptchaToken(token);
							setErrorMessage("");
						},
						"expired-callback": () => {
							setCaptchaToken("");
						},
						"error-callback": () => {
							setCaptchaToken("");
							setErrorMessage("No se pudo validar reCAPTCHA.");
						},
					});
					captchaWidgetIdRef.current = widgetId;
				}
				setCaptchaLoading(false);
				if (pollInterval) clearInterval(pollInterval);
			} catch (error) {
				if (container.querySelector("iframe")) {
					setCaptchaLoading(false);
					if (pollInterval) clearInterval(pollInterval);
				}
			}
		};

		const ensureCaptchaScript = () => {
			const existingScript = document.querySelector(
				'script[src*="recaptcha/api.js"]',
			);

			if (existingScript) {
				return existingScript;
			}

			const script = document.createElement("script");
			script.src =
				"https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoaded&render=explicit";
			script.async = true;
			script.defer = true;
			script.onerror = () => {
				if (!mounted) return;
				setCaptchaLoading(false);
				setErrorMessage("No se pudo cargar reCAPTCHA.");
			};
			document.body.appendChild(script);
			return script;
		};

		// Global hook called by Google's onload in index.html
		window.__renderCaptchaCallback = renderCaptchaWidget;

		ensureCaptchaScript();

		// Try rendering immediately if already loaded
		renderCaptchaWidget();

		// Active polling fallback every 100ms for up to 10 seconds
		let elapsed = 0;
		pollInterval = setInterval(() => {
			elapsed += 100;
			renderCaptchaWidget();

			if (elapsed >= 10000) {
				clearInterval(pollInterval);
				if (mounted && captchaWidgetIdRef.current === null) {
					setCaptchaLoading(false);
					setErrorMessage(
						"No se pudo cargar la validación reCAPTCHA. Verifique su conexión.",
					);
				}
			}
		}, 100);

		return () => {
			mounted = false;
			if (pollInterval) clearInterval(pollInterval);
			if (window.__renderCaptchaCallback === renderCaptchaWidget) {
				delete window.__renderCaptchaCallback;
			}
		};
	}, []);

	const resetCaptcha = () => {
		setCaptchaToken("");
		if (window.grecaptcha?.reset && captchaWidgetIdRef.current !== null) {
			try {
				window.grecaptcha.reset(captchaWidgetIdRef.current);
			} catch (error) {
				console.error("Error resetting reCAPTCHA:", error);
			}
		}
	};

	const handleSearch = async (event) => {
		event.preventDefault();

		if (!serialNumber.trim()) {
			setResults([]);
			setHasSearched(false);
			setErrorMessage("");
			return;
		}

		if (!captchaToken) {
			setErrorMessage("Confirme el reCAPTCHA para consultar.");
			return;
		}

		setResults([]);
		setHasSearched(false);
		setCopiedSerial("");
		setErrorMessage("");
		setIsLoading(true);

		try {
			const response = await runGetPublicLicense({
				serial: serialNumber.trim(),
			});
			const licenceRows = response?.message?.licenses ?? [];

			if (licenceRows.length > 0) {
				setResults(licenceRows);
				setHasSearched(true);
				return;
			}

			setResults([]);
			setHasSearched(true);
			setErrorMessage("Licencia no encontrada.");
		} catch (error) {
			setHasSearched(true);
			setErrorMessage(translatePublicLookupError(error));
		} finally {
			setIsLoading(false);
			resetCaptcha();
		}
	};

	const handleCopyLicense = async (license, serial) => {
		if (!license) {
			return;
		}

		try {
			await navigator.clipboard.writeText(license);
			setCopiedSerial(serial);
			window.setTimeout(() => setCopiedSerial(""), 1500);
		} catch {
			setErrorMessage("No se pudo copiar la licencia.");
		}
	};

	return (
		<div className={styles.consultaContainer}>
			<div className={styles.heroLayout}>
				<section
					className={styles.topLeft}
					data-testid="landing-hero-image"
				>
					<div className={styles.bannerStage}>
						<img
							src={runtBanner}
							alt="RUNT biométrico"
							className={styles.bannerImage}
						/>
					</div>
				</section>

				<section className={styles.topRight}>
					<div className={styles.copyBlock}>
						<h1 className={styles.title}>
							<span>URGENTE!!!</span>
							<span>
								QUE NO SE INTERRUMPAN TUS TRÁMITES EN EL RUNT
							</span>
						</h1>
						<h2 className={styles.subtitle}>
							ACTUALIZA TUS DISPOSITIVOS BIOMÉTRICOS PARA RUNT 2.0
						</h2>
						<div className={styles.description}>
							<p>
								Dirigido a Organismos de Tránsito, CDA, CRC,
								CEA, CIA y demás entidades que interactúan con
								el RUNT.
							</p>
							<p>
								Según la Circular Externa 20254000000817 del
								Ministerio de Transporte, a partir del 1 de
								julio de 2026 únicamente podrán interactuar con
								el sistema RUNT los dispositivos biométricos que
								cumplan los nuevos requerimientos técnicos y de
								homologación.
							</p>
							<p>No esperes hasta el último momento.</p>
							<p>
								<a
									className={styles.referenceLink}
									href={deviceReferencePdf}
									target="_blank"
									rel="noreferrer"
								>
									Haz clic aquí
								</a>
								, identifica la referencia de tu huellero y
								actualiza hoy tus dispositivos biométricos con
								Identica para garantizar la continuidad de tu
								operación.
							</p>
						</div>
						<ul className={styles.checkList}>
							<li>COMPATIBLE CON RUNT 2.0</li>
							<li>HOMOLOGADO POR LA RNEC</li>
							<li>LISTO PARA OPERAR DESDE EL 1 DE JULIO</li>
						</ul>
					</div>
				</section>

				<section className={styles.bottomLeft}>
					<div className={styles.mainCard}>
						<div className={styles.headerArea}>
							<h1 className={styles.formTitle}>
								Consulta licencia RUNT
							</h1>
							<p className={styles.formSubtitle}>
								Esta página consulta únicamente la licencia de
								RUNT válida para clientes que ya tienen una
								licencia activa o acaban de adquirirla.
							</p>
						</div>

						<form
							onSubmit={handleSearch}
							className={styles.searchForm}
						>
							<div className={styles.inputRow}>
								<TextInput
									id="serial"
									type="text"
									label="Serial"
									maxLength={11}
									placeholder="Ingrese el número de serial..."
									value={serialNumber}
									onChange={(value) =>
										setSerialNumber(
											`${value || ""}`
												.toUpperCase()
												.replace(/\s+/g, "")
												.slice(0, 11),
										)
									}
									className={styles.fieldGroup}
									inputClassName={styles.fieldInput}
									hideFeedback
								/>

								<button
									type="submit"
									className={styles.submitBtn}
								>
									{isLoading ? "Consultando..." : "Consultar"}
								</button>
							</div>

							<div className={styles.captchaBlock}>
								<span className={styles.captchaLabel}>
									Validación reCAPTCHA
								</span>
								{captchaLoading ? (
									<span className={styles.captchaLoading}>
										Cargando...
									</span>
								) : null}
								<div
									ref={captchaContainerRef}
									className={styles.captchaPlaceholder}
								/>
							</div>
						</form>
					</div>
				</section>

				<section className={styles.bottomRight}>
					<div className={styles.resultsPanel}>
						{errorMessage ? (
							<div className={styles.errorBox} role="alert">
								{errorMessage}
							</div>
						) : null}

						{hasSearched ? (
							<div className={styles.tableWrapper}>
								<table className={styles.resultsTable}>
									<thead>
										<tr>
											{resultColumns.map((column) => (
												<th key={column.key}>
													{column.label}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{results.length > 0
											? results.map((row) => (
													<tr key={row.serial}>
														<td>{row.company}</td>
														<td>
															<div
																className={
																	styles.scrollCell
																}
															>
																{row.serial}
															</div>
														</td>
														<td>
															{row.deviceModel
																?.name || "-"}
														</td>
														<td>
															{formatDate(
																row.purchaseDate,
															)}
														</td>
														<td>
															{formatDate(
																row.expirationDate,
															)}
														</td>
														<td>
															<div
																className={
																	styles.scrollCell
																}
															>
																<span
																	className={
																		styles.licenseCell
																	}
																>
																	{
																		row.license
																	}
																</span>
															</div>
														</td>
														<td
															className={
																styles.copyCell
															}
														>
															<button
																type="button"
																className={
																	styles.copyButton
																}
																onClick={() =>
																	handleCopyLicense(
																		row.license,
																		row.serial,
																	)
																}
															>
																{copiedSerial ===
																row.serial
																	? "Copiado"
																	: "Copiar"}
															</button>
														</td>
													</tr>
												))
											: null}
									</tbody>
								</table>
							</div>
						) : null}
					</div>
				</section>
			</div>
			<div className={styles.footerNotice}>
				Si al realizar la consulta le muestra el mensaje &quot;Licencia
				no encontrada&quot; por favor dirijase a{" "}
				<a href="https://identica.co/" target="_blank" rel="noreferrer">
					https://identica.co/
				</a>{" "}
				o pongase en contacto a los telefonos y WhatsApp{" "}
				<a href="tel:+573102757524">3102757524</a>,{" "}
				<a href="tel:+576014320720">6014320720</a> o al correo{" "}
				<a href="mailto:mcruz@identica-sa.com">mcruz@identica-sa.com</a>
				.
			</div>
		</div>
	);
};

export default Landing;
